import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion';
import styles from './PathwayPlanner.module.css';
import { TargetIcon, ClipboardIcon, SearchIcon, ChartIcon, BookIcon, CalculatorIcon } from './Icons';
import { useAutoSave } from '../hooks/useAutoSave';
import enhancedMilestoneAPI from '../services/enhancedMilestoneAPI.js';
import GoalTimelineChart from './GoalTimelineChart';
import EnhancedMilestoneDisplay from './EnhancedMilestoneDisplay';

const PathwayPlanner = () => {
  const { isAuthenticated, user } = useAuth0();
  
  const [goalInput, setGoalInput] = useState('');
  const [goalCategory, setGoalCategory] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [userGoals, setUserGoals] = useState([]);
  const [expandedGoals, setExpandedGoals] = useState({});
  const [editingGoal, setEditingGoal] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editDetails, setEditDetails] = useState('');
  const [loadingAiSuggestions, setLoadingAiSuggestions] = useState({});
  const [highlightedMilestone, setHighlightedMilestone] = useState(null);
  const [formProgress, setFormProgress] = useState(0);
  const [draftSaved, setDraftSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [dateError, setDateError] = useState('');
  const [editDateError, setEditDateError] = useState('');
  
  // Enhanced user context state
  const [userProfile, setUserProfile] = useState({});
  const [collegeActivities, setCollegeActivities] = useState([]);
  const [autoDetectedCategory, setAutoDetectedCategory] = useState(false);
  
  const saveTimeout = useRef();

  // Auto-save goals
  useAutoSave('userGoals', userGoals);
  
  // Load user data on component mount
  useEffect(() => {
    if (isAuthenticated) {
      // Load user profile
      const savedProfile = JSON.parse(localStorage.getItem('academicProfile')) || {};
      setUserProfile(savedProfile);
      
      // Load college activities
      const savedActivities = JSON.parse(localStorage.getItem('collegeActivities')) || [];
      setCollegeActivities(savedActivities);
      
      // Load existing goals
      const savedGoals = JSON.parse(localStorage.getItem('userGoals')) || [];
      setUserGoals(savedGoals);
    }
  }, [isAuthenticated]);
  
  // Predefined categories with icons
  const categories = [
    { id: 'health', name: 'Health & Fitness', icon: 'fitness' },
    { id: 'career', name: 'Career Growth', icon: 'career' },
    { id: 'investment banking', name: 'Investment Banking', icon: 'finance' },
    { id: 'academics', name: 'Academics', icon: 'learning' },
    { id: 'finance', name: 'Finance', icon: 'finance' },
    { id: 'personal', name: 'Personal Development', icon: 'personal' },
    { id: 'relationships', name: 'Relationships', icon: 'relationships' },
    { id: 'hobbies', name: 'Hobbies & Interests', icon: 'hobbies' },
    { id: 'other', name: 'Other', icon: 'other' }
  ];

  // Intelligent category detection based on goal title
  const detectCategoryFromTitle = (title) => {
    const titleLower = title.toLowerCase();
    
    // Investment banking specific detection
    if (titleLower.includes('investment banking') || 
        titleLower.includes('wall street') || 
        titleLower.includes('banking') ||
        titleLower.includes('financial modeling') ||
        titleLower.includes('m&a') ||
        titleLower.includes('mergers') ||
        titleLower.includes('acquisitions') ||
        titleLower.includes('capital markets') ||
        titleLower.includes('goldman') ||
        titleLower.includes('morgan stanley') ||
        titleLower.includes('jpmorgan')) {
      return 'investment banking';
    }
    
    // Career detection
    if (titleLower.includes('career') || 
        titleLower.includes('job') || 
        titleLower.includes('work') ||
        titleLower.includes('employment') ||
        titleLower.includes('professional') ||
        titleLower.includes('industry') ||
        titleLower.includes('position') ||
        titleLower.includes('role')) {
      return 'career';
    }
    
    // Academic detection
    if (titleLower.includes('study') || 
        titleLower.includes('learn') || 
        titleLower.includes('academic') ||
        titleLower.includes('college') ||
        titleLower.includes('university') ||
        titleLower.includes('school') ||
        titleLower.includes('degree') ||
        titleLower.includes('course') ||
        titleLower.includes('certification') ||
        titleLower.includes('gpa') ||
        titleLower.includes('grade')) {
      return 'academics';
    }
    
    // Health detection
    if (titleLower.includes('health') || 
        titleLower.includes('fitness') || 
        titleLower.includes('exercise') ||
        titleLower.includes('workout') ||
        titleLower.includes('diet') ||
        titleLower.includes('nutrition') ||
        titleLower.includes('weight') ||
        titleLower.includes('muscle') ||
        titleLower.includes('run') ||
        titleLower.includes('marathon') ||
        titleLower.includes('gym')) {
      return 'health';
    }
    
    // Finance detection
    if (titleLower.includes('money') || 
        titleLower.includes('finance') || 
        titleLower.includes('financial') ||
        titleLower.includes('save') ||
        titleLower.includes('invest') ||
        titleLower.includes('budget') ||
        titleLower.includes('debt') ||
        titleLower.includes('income') ||
        titleLower.includes('salary') ||
        titleLower.includes('wealth')) {
      return 'finance';
    }
    
    // Personal development detection
    if (titleLower.includes('personal') || 
        titleLower.includes('development') || 
        titleLower.includes('growth') ||
        titleLower.includes('improve') ||
        titleLower.includes('skill') ||
        titleLower.includes('confidence') ||
        titleLower.includes('leadership') ||
        titleLower.includes('communication')) {
      return 'personal';
    }
    
    // Relationship detection
    if (titleLower.includes('relationship') || 
        titleLower.includes('friend') || 
        titleLower.includes('family') ||
        titleLower.includes('social') ||
        titleLower.includes('network') ||
        titleLower.includes('connect') ||
        titleLower.includes('dating') ||
        titleLower.includes('romantic')) {
      return 'relationships';
    }
    
    // Hobby detection
    if (titleLower.includes('hobby') || 
        titleLower.includes('interest') || 
        titleLower.includes('passion') ||
        titleLower.includes('art') ||
        titleLower.includes('music') ||
        titleLower.includes('sport') ||
        titleLower.includes('craft') ||
        titleLower.includes('game') ||
        titleLower.includes('collect')) {
      return 'hobbies';
    }
    
    // Default to career for professional goals
    return 'career';
  };

  // Auto-detect category when goal input changes
  useEffect(() => {
    if (goalInput.trim() && !goalCategory) {
      const detectedCategory = detectCategoryFromTitle(goalInput);
      setGoalCategory(detectedCategory);
      setAutoDetectedCategory(true); // Set to true when auto-detection happens
    }
  }, [goalInput, goalCategory]);

  // Reset auto-detection flag when user manually selects a category
  const handleCategorySelect = (categoryId) => {
    setGoalCategory(categoryId);
    setAutoDetectedCategory(false); // Reset when user manually selects
  };
  
  const goalColors = [
    '#3B82F6', // Blue
    '#06B6D4', // Cyan
    '#8B5CF6', // Violet
    '#10B981', // Emerald
    '#6366F1', // Indigo
    '#0EA5E9', // Sky Blue
    '#14B8A6', // Teal
    '#7C3AED'  // Purple
  ];
  
  // Helper function to validate date is not in the past
  const validateDate = (dateString) => {
    if (!dateString) return { isValid: true, error: '' };
    
    const selectedDate = new Date(dateString);
    const today = new Date();
    
    // Reset time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return { isValid: false, error: 'Completion date cannot be in the past' };
    }
    
    return { isValid: true, error: '' };
  };

  // Get today's date in YYYY-MM-DD format for max attribute
  const getTodayString = () => {
    return new Date().toISOString().split('T')[0];
  };
  
  // Calculate form progress
  useEffect(() => {
    let progress = 0;
    if (goalInput.trim()) progress += 25;
    if (goalCategory) progress += 25;
    if (goalDescription.trim()) progress += 25;
    if (completionDate && validateDate(completionDate).isValid) progress += 25;
    setFormProgress(progress);
  }, [goalInput, goalCategory, goalDescription, completionDate]);

  // Auto-save draft functionality (debounced)
  useEffect(() => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    const draft = { goalInput, goalCategory, goalDescription, completionDate };
    const hasContent = Object.values(draft).some(value => value && value.trim());
    if (hasContent) {
      saveTimeout.current = setTimeout(() => {
        localStorage.setItem('pathwayPlannerDraft', JSON.stringify(draft));
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 2000);
      }, 1000); // 1 second debounce
    }
    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
    };
  }, [goalInput, goalCategory, goalDescription, completionDate]);

  // Load draft on component mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('pathwayPlannerDraft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setGoalInput(draft.goalInput || '');
        setGoalCategory(draft.goalCategory || '');
        setGoalDescription(draft.goalDescription || '');
        setCompletionDate(draft.completionDate || '');
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, []);
  
  // Function to get milestone color based on goal index and milestone index
  // const getMilestoneColor = (goalIndex, milestoneIndex) => {
  //   // Create a unique index by combining goal and milestone indices
  //   const colorIndex = (goalIndex * 7 + milestoneIndex) % coolMilestoneColors.length;
  //   return coolMilestoneColors[colorIndex];
  // };

  const handleSubmitGoal = async (e) => {
    e.preventDefault();
    if (goalInput.trim() === '' || completionDate.trim() === '') return;
    if (!goalCategory) {
      alert('Please select a category for your goal.');
      return;
    }
    // Validate date before submission
    const dateValidation = validateDate(completionDate);
    if (!dateValidation.isValid) {
      setDateError(dateValidation.error);
      return;
    }
    setIsSubmitting(true);
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 800));
    // Assign a color to the new goal (cycle through available colors)
    const goalColor = goalColors[userGoals.length % goalColors.length];
    const newGoal = {
      title: goalInput,
      dueDate: completionDate,
      category: goalCategory || 'General',
      status: goalCategory || 'General',
      details: goalDescription,
      aiSuggestions: null,
      color: goalColor
    };
    setUserGoals([...userGoals, newGoal]);
    setGoalInput('');
    setGoalCategory('');
    setGoalDescription('');
    setCompletionDate('');
    setDateError('');
    setAutoDetectedCategory(false); // Reset auto-detection flag
    // Clear draft after successful submission
    localStorage.removeItem('pathwayPlannerDraft');
    setIsSubmitting(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleDeleteGoal = (index) => {
    const updatedGoals = [...userGoals];
    updatedGoals.splice(index, 1);
    setUserGoals(updatedGoals);
    
    // If the deleted goal was expanded, remove it from expanded state
    if (expandedGoals[index]) {
      const updatedExpandedGoals = { ...expandedGoals };
      delete updatedExpandedGoals[index];
      
      // Adjust the indices of the expanded goals
      const newExpandedGoals = {};
      Object.keys(updatedExpandedGoals).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum > index) {
          newExpandedGoals[keyNum - 1] = updatedExpandedGoals[key];
        } else {
          newExpandedGoals[key] = updatedExpandedGoals[key];
        }
      });
      
      setExpandedGoals(newExpandedGoals);
    }
  };

  const startEditGoal = (index) => {
    const goal = userGoals[index];
    setEditingGoal(index);
    setEditTitle(goal.title);
    setEditDueDate(goal.dueDate);
    setEditStatus(goal.status);
    setEditDetails(goal.details || '');
  };

  const saveEditGoal = () => {
    if (editingGoal === null) return;
    // Validate date before saving
    const dateValidation = validateDate(editDueDate);
    if (!dateValidation.isValid) {
      setEditDateError(dateValidation.error);
      return;
    }
    const updatedGoals = [...userGoals];
    updatedGoals[editingGoal] = {
      ...updatedGoals[editingGoal],
      title: editTitle,
      dueDate: editDueDate,
      category: updatedGoals[editingGoal].category || goalCategory || 'academics', // preserve or set category
      status: editStatus,
      details: editDetails
    };
    setUserGoals(updatedGoals);
    setEditingGoal(null);
    setEditDateError('');
  };

  const cancelEditGoal = () => {
    setEditingGoal(null);
    setEditDateError('');
  };

  const toggleGoalExpand = (index) => {
    const newExpandedState = !expandedGoals[index];
    
    setExpandedGoals({
      ...expandedGoals,
      [index]: newExpandedState
    });
    
    // If we're expanding and there are no AI suggestions yet, fetch them
    if (newExpandedState && !userGoals[index].aiSuggestions && !loadingAiSuggestions[index]) {
      getAiSuggestions(index);
    }
  };

  // Enhanced user context collection for ultra-specific recommendations
  const buildEnhancedUserContext = (goal) => {
    const userProfile = JSON.parse(localStorage.getItem('academicProfile')) || {};
    const collegeActivities = JSON.parse(localStorage.getItem('collegeActivities')) || [];
    
    // Enhanced ultra-specific context collection
    const context = {
      // Core user information
      currentLevel: goal.category === 'academics' ? 'High School' : 'Not specified',
      location: userProfile.location || 'Not specified',
      budget: userProfile.budget || 'Flexible',
      timeAvailability: userProfile.timeAvailability || 'Flexible schedule',
      
      // Academic-specific ultra-detailed context
      grade: userProfile.grade || 'Not specified',
      gpa: userProfile.gpa || 'Not specified',
      testScores: userProfile.sat || userProfile.act || 'Not specified',
      extracurriculars: collegeActivities.length > 0 ? 
        collegeActivities.map(act => act.title || act.name).join(', ') : 'None listed',
      techExperience: userProfile.techExperience || 'Beginner',
      
      // Career-specific ultra-detailed context
      currentRole: userProfile.currentRole || 'Not specified',
      industry: userProfile.industry || 'Not specified',
      experienceYears: userProfile.experienceYears || 'Not specified',
      currentSalary: userProfile.currentSalary || 'Not specified',
      currentSkills: userProfile.currentSkills || 'Basic level',
      education: userProfile.education || 'Not specified',
      networkStrength: userProfile.networkStrength || 'Limited',
      
      // Health-specific ultra-detailed context
      age: userProfile.age || 'Not specified',
      currentStats: userProfile.currentStats || 'Not specified',
      healthConditions: userProfile.healthConditions || 'None',
      exerciseExperience: userProfile.exerciseExperience || 'Beginner',
      nutritionKnowledge: userProfile.nutritionKnowledge || 'Basic',
      equipmentAccess: userProfile.equipmentAccess || 'Limited',
      
      // Finance-specific ultra-detailed context
      currentSavings: userProfile.currentSavings || 'Not specified',
      currentDebt: userProfile.currentDebt || 'Not specified',
      creditScore: userProfile.creditScore || 'Not specified',
      investmentExperience: userProfile.investmentExperience || 'Beginner',
      financialGoals: userProfile.financialGoals || 'Not specified',
      riskTolerance: userProfile.riskTolerance || 'Moderate',
      timeHorizon: userProfile.timeHorizon || 'Not specified',
      
      // Personal development ultra-detailed context
      currentSkillLevel: userProfile.currentSkillLevel || 'Beginner',
      availableTime: userProfile.availableTime || 'Flexible',
      learningStyle: userProfile.learningStyle || 'Visual',
      motivationLevel: userProfile.motivationLevel || 'Moderate',
      supportSystem: userProfile.supportSystem || 'Limited',
      previousExperience: userProfile.previousExperience || 'Limited',
      interests: userProfile.interests || 'General interests',
      
      // Relationship ultra-detailed context
      relationshipStatus: userProfile.relationshipStatus || 'Single',
      socialCircle: userProfile.socialCircle || 'School friends',
      communicationSkills: userProfile.communicationSkills || 'Basic',
      socialAnxiety: userProfile.socialAnxiety || 'Low',
      relationshipGoals: userProfile.relationshipGoals || 'Build connections',
      
      // Hobby ultra-detailed context
      spaceAvailable: userProfile.spaceAvailable || 'Limited',
      physicalLimitations: userProfile.physicalLimitations || 'None',
      
      // Enhanced context for ultra-specific recommendations
      // Academic enhancements
      currentCourses: userProfile.currentCourses || 'Standard curriculum',
      targetSchools: userProfile.targetSchools || 'Not specified',
      academicStrengths: userProfile.academicStrengths || 'Not specified',
      academicWeaknesses: userProfile.academicWeaknesses || 'Not specified',
      studyHabits: userProfile.studyHabits || 'Basic',
      testPrepExperience: userProfile.testPrepExperience || 'None',
      researchExperience: userProfile.researchExperience || 'None',
      leadershipExperience: userProfile.leadershipExperience || 'Limited',
      
      // Career enhancements
      targetCompanies: userProfile.targetCompanies || 'Not specified',
      desiredSalary: userProfile.desiredSalary || 'Not specified',
      preferredLocation: userProfile.preferredLocation || 'Flexible',
      technicalSkills: userProfile.technicalSkills || 'Basic',
      softSkills: userProfile.softSkills || 'Basic',
      certifications: userProfile.certifications || 'None',
      portfolio: userProfile.portfolio || 'None',
      industryConnections: userProfile.industryConnections || 'Limited',
      
      // Health enhancements
      currentWeight: userProfile.currentWeight || 'Not specified',
      targetWeight: userProfile.targetWeight || 'Not specified',
      bodyFatPercentage: userProfile.bodyFatPercentage || 'Not specified',
      muscleMass: userProfile.muscleMass || 'Not specified',
      fitnessGoals: userProfile.fitnessGoals || 'General fitness',
      dietaryRestrictions: userProfile.dietaryRestrictions || 'None',
      sleepSchedule: userProfile.sleepSchedule || 'Standard',
      stressLevel: userProfile.stressLevel || 'Moderate',
      medicalConditions: userProfile.medicalConditions || 'None',
      currentMedications: userProfile.currentMedications || 'None',
      
      // Finance enhancements
      monthlyIncome: userProfile.monthlyIncome || 'Not specified',
      monthlyExpenses: userProfile.monthlyExpenses || 'Not specified',
      emergencyFund: userProfile.emergencyFund || 'Not specified',
      retirementAccounts: userProfile.retirementAccounts || 'None',
      investmentAccounts: userProfile.investmentAccounts || 'None',
      debtTypes: userProfile.debtTypes || 'None',
      insuranceCoverage: userProfile.insuranceCoverage || 'Basic',
      taxSituation: userProfile.taxSituation || 'Standard',
      
      // Personal development enhancements
      learningGoals: userProfile.learningGoals || 'General improvement',
      skillGaps: userProfile.skillGaps || 'Not specified',
      timeManagement: userProfile.timeManagement || 'Basic',
      productivityLevel: userProfile.productivityLevel || 'Moderate',
      stressManagement: userProfile.stressManagement || 'Basic',
      confidenceLevel: userProfile.confidenceLevel || 'Moderate',
      publicSpeaking: userProfile.publicSpeaking || 'Limited',
      creativityLevel: userProfile.creativityLevel || 'Moderate',
      
      // Relationship enhancements
      communicationStyle: userProfile.communicationStyle || 'Friendly',
      conflictResolution: userProfile.conflictResolution || 'Basic',
      empathyLevel: userProfile.empathyLevel || 'Moderate',
      socialConfidence: userProfile.socialConfidence || 'Moderate',
      networkingExperience: userProfile.networkingExperience || 'Limited',
      relationshipHistory: userProfile.relationshipHistory || 'Limited',
      socialActivities: userProfile.socialActivities || 'Basic',
      
      // Hobby enhancements
      currentHobbies: userProfile.currentHobbies || 'None',
      skillLevel: userProfile.skillLevel || 'Beginner',
      availableBudget: userProfile.availableBudget || 'Limited',
      workspaceSize: userProfile.workspaceSize || 'Small',
      toolsAvailable: userProfile.toolsAvailable || 'Basic',
      communityAccess: userProfile.communityAccess || 'Limited',
      competitionInterest: userProfile.competitionInterest || 'Low',
      
      // Seasonal and timing context
      currentSeason: new Date().toLocaleDateString('en-US', { month: 'long' }),
      academicYear: userProfile.academicYear || 'Current year',
      applicationDeadlines: userProfile.applicationDeadlines || 'Not specified',
      internshipCycles: userProfile.internshipCycles || 'Not specified',
      competitionDates: userProfile.competitionDates || 'Not specified',
      
      // Regional and local context
      localResources: userProfile.localResources || 'Not specified',
      communityPrograms: userProfile.communityPrograms || 'Not specified',
      localEvents: userProfile.localEvents || 'Not specified',
      regionalOpportunities: userProfile.regionalOpportunities || 'Not specified',
      
      // Technology and digital context
      techAccess: userProfile.techAccess || 'Basic',
      onlineLearning: userProfile.onlineLearning || 'Comfortable',
      digitalSkills: userProfile.digitalSkills || 'Basic',
      socialMediaPresence: userProfile.socialMediaPresence || 'Limited',
      
      // Support and accountability context
      accountabilityPartner: userProfile.accountabilityPartner || 'None',
      mentorAccess: userProfile.mentorAccess || 'Limited',
      familySupport: userProfile.familySupport || 'Moderate',
      friendSupport: userProfile.friendSupport || 'Moderate',
      professionalSupport: userProfile.professionalSupport || 'Limited',
      
      // Motivation and commitment context
      motivationSource: userProfile.motivationSource || 'Personal growth',
      commitmentLevel: userProfile.commitmentLevel || 'Moderate',
      obstacleAwareness: userProfile.obstacleAwareness || 'Basic',
      resilienceLevel: userProfile.resilienceLevel || 'Moderate',
      goalClarity: userProfile.goalClarity || 'Moderate'
    };
    
    return context;
  };

  const getAiSuggestions = async (index) => {
    const goal = userGoals[index];
    if (!goal) return;
    
    setLoadingAiSuggestions(prev => ({ ...prev, [index]: true }));
    
    try {
      // Build enhanced user context for ultra-specific recommendations
      const userContext = buildEnhancedUserContext(goal);
      
      console.log('Generating AI suggestions with enhanced context:', {
        goal: goal.title,
        category: goal.category,
        context: userContext
      });

      // Generate enhanced milestones
      const result = await enhancedMilestoneAPI.generateEnhancedMilestones(goal, userContext);
      
      // Debug logging to see what we're getting
      console.log('Full result from backend:', result);
      console.log('Result data:', result.data);
      console.log('Result success:', result.success);
      console.log('Result error:', result.error);
      console.log('Result metadata:', result.metadata);
      
      // Check if we have data, regardless of validation status
      if (result.data && Object.keys(result.data).length > 0) {
        // Update the goal with enhanced suggestions
        const updatedGoals = [...userGoals];
        updatedGoals[index] = {
          ...updatedGoals[index],
          aiSuggestions: JSON.stringify(result.data),
          aiMetadata: result.metadata,
          aiQuality: result.quality
        };
        
        setUserGoals(updatedGoals);
        console.log('AI suggestions generated successfully for:', goal.title);
        
        // Show warning if validation failed but we still have data
        if (result.metadata?.validation_errors && result.metadata.validation_errors.length > 0) {
          console.warn('Validation warnings:', result.metadata.validation_errors);
        }
      } else {
        console.error('Failed to generate AI suggestions: No data received');
        alert('Failed to generate suggestions: No data received from AI service');
      }
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
      alert('Error generating suggestions. Please try again.');
    } finally {
      setLoadingAiSuggestions(prev => ({ ...prev, [index]: false }));
    }
  };

  const requestNewSuggestions = (index) => {
    // Clear existing suggestions and request new ones
    const updatedGoals = [...userGoals];
    updatedGoals[index] = {
      ...updatedGoals[index],
      aiSuggestions: null
    };
    
    setUserGoals(updatedGoals);
    getAiSuggestions(index);
  };

  // Parse the AI suggestions JSON if it exists
  const parseAiSuggestions = (jsonString) => {
    if (!jsonString) return null;
    
    try {
      // For real API responses that might be returned as strings
      const suggestions = typeof jsonString === 'string' 
        ? JSON.parse(jsonString) 
        : jsonString;
      
      return suggestions;
    } catch (error) {
      console.error('Error parsing AI suggestions:', error);
      return null;
    }
  };

  // Handle milestone click from chart
  const handleMilestoneClick = (goalIndex, milestoneIndex, milestoneKey) => {
    // First, expand the goal if it's not already expanded
    if (!expandedGoals[goalIndex]) {
      setExpandedGoals({
        ...expandedGoals,
        [goalIndex]: true
      });
      
      // If no AI suggestions yet, fetch them
      if (!userGoals[goalIndex].aiSuggestions && !loadingAiSuggestions[goalIndex]) {
        getAiSuggestions(goalIndex);
      }
    }
    
    // Set the highlighted milestone
    setHighlightedMilestone({ goalIndex, milestoneIndex, milestoneKey });
    
    // Scroll to the milestone with a slight delay to ensure the content is expanded
    setTimeout(() => {
      const milestoneElement = document.getElementById(`milestone-${goalIndex}-${milestoneIndex}`);
      if (milestoneElement) {
        // Scroll to the element with smooth behavior
        milestoneElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        
        // Clear highlight after 3 seconds
        setTimeout(() => {
          setHighlightedMilestone(null);
        }, 3000);
      }
    }, expandedGoals[goalIndex] ? 100 : 1200); // Longer delay if we need to fetch AI suggestions
  };

  // SVG Icons
  const getSvgIcon = (iconName) => {
    const icons = {
      fitness: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 6.5L17.5 17.5M17.5 6.5L6.5 17.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M12 2L12 22M2 12L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      career: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 7L10 2L2 7L10 12L20 7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
          <path d="M2 17L10 22L20 17" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
          <path d="M2 12L10 17L20 12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
        </svg>
      ),
      learning: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 19.9391 11.5786 18.9217 10.8284 18.1716C10.0783 17.4214 9.06087 17 8 17H2V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 19.9391 12.4214 18.9217 13.1716 18.1716C13.9217 17.4214 14.9391 17 16 17H22V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      finance: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      personal: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      relationships: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2"/>
          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      hobbies: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      other: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      target: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      tag: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9625 21.1666 11.4696 21.1666 12C21.1666 12.5304 20.9625 13.0375 20.59 13.41Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      edit: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.17 3.33H3.33C2.89 3.33 2.47 3.51 2.15 3.82C1.84 4.13 1.67 4.55 1.67 5V16.67C1.67 17.11 1.84 17.53 2.15 17.85C2.47 18.16 2.89 18.33 3.33 18.33H15C15.44 18.33 15.86 18.16 16.18 17.85C16.49 17.53 16.67 17.11 16.67 16.67V10.83" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M15.42 2.08C15.75 1.75 16.2 1.57 16.67 1.57C17.14 1.57 17.59 1.75 17.92 2.08C18.25 2.41 18.43 2.86 18.43 3.33C18.43 3.8 18.25 4.25 17.92 4.58L10 12.5L6.67 13.33L7.5 10L15.42 2.08Z" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      calendar: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2.5" y="3.33" width="15" height="15" rx="1.67" ry="1.67" stroke="currentColor" strokeWidth="1.67"/>
          <line x1="13.33" y1="1.67" x2="13.33" y2="5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round"/>
          <line x1="6.67" y1="1.67" x2="6.67" y2="5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round"/>
          <line x1="2.5" y1="8.33" x2="17.5" y2="8.33" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round"/>
        </svg>
      ),
      check: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      save: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16L21 8V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <polyline points="17,21 17,13 7,13 7,21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <polyline points="7,3 7,8 15,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      sparkle: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    };
    return icons[iconName] || icons.other;
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Progress Indicator */}
        <div className={styles.progressSection}>
          <div className={styles.progressHeader}>
            <h1 className={styles.heading}>Create Your Goal Pathway</h1>
            <div className={styles.progressInfo}>
              <span className={styles.progressText}>Step {Math.ceil(formProgress / 25)} of 4</span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${formProgress}%` }}
                ></div>
              </div>
            </div>
          </div>
          {draftSaved && (
            <div className={styles.draftSaved}>
              <span className={styles.draftIcon}>{getSvgIcon('save')}</span>
              Draft saved automatically
            </div>
          )}
        </div>

        {/* Modern Form Card */}
        <div className={styles.formCard}>
          {showSuccess && (
            <div className={styles.successNotification}>
              <div className={styles.successIcon}>{getSvgIcon('sparkle')}</div>
              <div className={styles.successContent}>
                <h3>Goal Created Successfully!</h3>
                <p>Your goal has been added to your timeline. Check it out below!</p>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmitGoal}>
            {/* Goal Title Section */}
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>{getSvgIcon('target')}</div>
                <div className={styles.sectionContent}>
                  <h2 className={styles.sectionTitle}>What's your goal?</h2>
                  <p className={styles.sectionDescription}>
                    Be specific and inspiring. What do you want to achieve?
                  </p>
                </div>
                {goalInput.trim() && (
                  <div className={styles.validationCheck} title="Goal title completed">{getSvgIcon('check')}</div>
                )}
              </div>
              <div className={styles.inputWrapper}>
                <input 
                  type="text" 
                  placeholder="e.g., Run a marathon, Learn Spanish, Start a business..." 
                  className={`${styles.modernInput} ${goalInput.trim() ? styles.valid : ''}`}
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  required
                  aria-label="Goal title"
                  aria-describedby="goal-title-help"
                  maxLength={100}
                />
                <div className={styles.inputIcon}>{getSvgIcon('edit')}</div>
                <div id="goal-title-help" className={styles.helpText}>
                  Keep it concise but descriptive. This will be the main title of your goal.
                </div>
              </div>
            </div>

            {/* Category Selection Section */}
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>{getSvgIcon('tag')}</div>
                <div className={styles.sectionContent}>
                  <h2 className={styles.sectionTitle}>Choose a category</h2>
                  <p className={styles.sectionDescription}>
                    This helps us provide better recommendations and organize your goals
                    {autoDetectedCategory && (
                      <span className={styles.autoDetectedBadge}>
                        ✓ Auto-detected based on your goal
                      </span>
                    )}
                  </p>
                </div>
                {goalCategory && (
                  <div className={styles.validationCheck} title="Category selected">{getSvgIcon('check')}</div>
                )}
              </div>
              <div className={styles.categoryGrid} role="group" aria-labelledby="category-label">
                <div id="category-label" className={styles.srOnly}>Goal categories</div>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={`${styles.categoryPill} ${goalCategory === category.id ? styles.selected : ''}`}
                    onClick={() => handleCategorySelect(category.id)}
                    aria-pressed={goalCategory === category.id}
                    title={`Select ${category.name} category`}
                  >
                    <span className={styles.categoryIcon} aria-hidden="true">{getSvgIcon(category.icon)}</span>
                    <span className={styles.categoryName}>{category.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Description Section */}
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>{getSvgIcon('edit')}</div>
                <div className={styles.sectionContent}>
                  <h2 className={styles.sectionTitle}>Describe your goal</h2>
                  <p className={styles.sectionDescription}>
                    What does success look like? How will you measure progress?
                  </p>
                </div>
                {goalDescription.trim() && (
                  <div className={styles.validationCheck} title="Description completed">{getSvgIcon('check')}</div>
                )}
              </div>
              <div className={styles.textareaWrapper}>
                <textarea 
                  placeholder="Describe your goal in detail. What motivates you? What obstacles might you face? How will you know when you've achieved it?"
                  className={`${styles.modernTextarea} ${goalDescription.trim() ? styles.valid : ''}`}
                  value={goalDescription}
                  onChange={(e) => setGoalDescription(e.target.value)}
                  rows={4}
                  maxLength={300}
                  aria-label="Goal description"
                  aria-describedby="description-help description-counter"
                />
                <div 
                  id="description-counter"
                  className={`${styles.characterCounter} ${goalDescription.length > 250 ? styles.warning : ''}`}
                >
                  {goalDescription.length}/300
                </div>
                <div id="description-help" className={styles.helpText}>
                  Be specific about what success means to you and how you'll track your progress.
                </div>
              </div>
            </div>

            {/* Completion Date Section */}
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>{getSvgIcon('calendar')}</div>
                <div className={styles.sectionContent}>
                  <h2 className={styles.sectionTitle}>When do you want to achieve this?</h2>
                  <p className={styles.sectionDescription}>
                    Set a realistic but challenging timeline
                  </p>
                </div>
                {completionDate && (
                  <div className={styles.validationCheck} title="Completion date set">{getSvgIcon('check')}</div>
                )}
              </div>
              <div className={styles.inputWrapper}>
                <input 
                  type="date" 
                  className={`${styles.modernInput} ${styles.dateInput} ${completionDate ? styles.valid : ''} ${dateError ? styles.error : ''}`}
                  value={completionDate}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setCompletionDate(newDate);
                    const validation = validateDate(newDate);
                    setDateError(validation.error);
                  }}
                  min={getTodayString()}
                  required
                  aria-label="Completion date"
                  aria-describedby="date-help"
                />
                <div className={styles.inputIcon}>{getSvgIcon('calendar')}</div>
                {dateError && (
                  <div className={styles.errorMessage} id="date-error">
                    {dateError}
                  </div>
                )}
                <div id="date-help" className={styles.helpText}>
                  Choose a date that gives you enough time but keeps you motivated. Today or any future date is acceptable.
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className={styles.submitSection}>
              <button 
                type="submit" 
                className={`${styles.modernSubmitButton} ${isSubmitting ? styles.loading : ''}`}
                disabled={isSubmitting || formProgress < 100}
                aria-describedby="submit-help"
              >
                {isSubmitting ? (
                  <>
                    <div className={styles.spinner} aria-hidden="true"></div>
                    <span>Creating your goal...</span>
                  </>
                ) : (
                  <>
                    <span className={styles.buttonIcon} aria-hidden="true">{getSvgIcon('sparkle')}</span>
                    <span>Create My Goal</span>
                  </>
                )}
              </button>
              {formProgress < 100 && (
                <p id="submit-help" className={styles.completionHint}>
                  Complete all fields to create your goal
                </p>
              )}
            </div>
          </form>
        </div>
        
        <div className={styles.timelineContainer}>
          <h2 className={styles.sectionTitle}>Your Timeline</h2>
          
          <div className={styles.timelineGraph}>
            {/* Recharts-based visualization */}
            {userGoals.length > 0 ? (
              <GoalTimelineChart 
                userGoals={userGoals}
                parseAiSuggestions={parseAiSuggestions}
                type="catmullRom"
                onMilestoneClick={handleMilestoneClick}
              />
            ) : (
              <div className={styles.emptyTimeline}>
                <div className={styles.emptyIcon}>{getSvgIcon('target')}</div>
                <h3>No goals yet</h3>
                <p>Add some goals above to start building your timeline!</p>
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.currentGoals}>
          <div className={styles.sectionHeaderWithHint}>
            <h2 className={styles.sectionTitle}>Current Goals</h2>
            {userGoals.length > 0 && (
              <div className={styles.milestoneHint}>
                <img src={process.env.PUBLIC_URL + "/bright-star.svg"} alt="AI" className={styles.aiIcon} />
                <span>Click "Generate Milestones" to expand and get AI-powered step-by-step plans!</span>
              </div>
            )}
          </div>
          
          <div className={styles.goalsList}>
            {userGoals.length === 0 ? (
              <div className={styles.emptyGoals}>
                <div className={styles.emptyIcon}>{getSvgIcon('target')}</div>
                <h3>No goals yet</h3>
                <p>Start by adding your first goal above!</p>
              </div>
            ) : (
              userGoals.map((goal, index) => (
                <div key={index} className={styles.goalCard}>
                  {editingGoal === index ? (
                    <div className={styles.editGoalForm}>
                      <div className={styles.inputGroup}>
                        <input 
                          type="text" 
                          className={styles.inputField} 
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder="Goal title"
                        />
                      </div>
                      <div className={styles.inputGroup}>
                        <input 
                          type="date" 
                          className={`${styles.inputField} ${editDateError ? styles.error : ''}`}
                          value={editDueDate}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            setEditDueDate(newDate);
                            const validation = validateDate(newDate);
                            setEditDateError(validation.error);
                          }}
                          min={getTodayString()}
                          placeholder="Due date"
                        />
                        {editDateError && (
                          <div className={styles.errorMessage}>
                            {editDateError}
                          </div>
                        )}
                      </div>
                      <div className={styles.inputGroup}>
                        <input 
                          type="text" 
                          className={styles.inputField} 
                          value={editStatus}
                          onChange={(e) => setEditStatus(e.target.value)}
                          placeholder="Status"
                        />
                      </div>
                      <div className={styles.inputGroup}>
                        <textarea 
                          className={styles.textareaField} 
                          value={editDetails}
                          onChange={(e) => setEditDetails(e.target.value)}
                          placeholder="Detailed plan"
                          rows={4}
                        />
                      </div>
                      <div className={styles.editActions}>
                        <button className={styles.saveButton} onClick={saveEditGoal}>Save</button>
                        <button className={styles.cancelButton} onClick={cancelEditGoal}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.goalHeader}>
                        <div className={styles.goalMainInfo}>
                          <h3 className={styles.goalTitle} style={{ color: goal.color }}>{goal.title}</h3>
                          <div className={styles.goalMetadata}>
                            <p className={styles.goalDueDate}>Due: {goal.dueDate}</p>
                            {goal.status && (
                              <div className={styles.goalStatus} style={{ backgroundColor: `${goal.color}25`, color: goal.color }}>
                                {goal.status}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={styles.goalActions}>
                          {/* Add prominent milestone generation button */}
                          {!goal.aiSuggestions && !loadingAiSuggestions[index] && (
                            <button 
                              className={`${styles.generateMilestonesButton} ${styles.attentionGrabber}`}
                              onClick={() => {
                                // First expand the goal if it's not already expanded
                                if (!expandedGoals[index]) {
                                  setExpandedGoals({
                                    ...expandedGoals,
                                    [index]: true
                                  });
                                }
                                // Then generate milestones
                                getAiSuggestions(index);
                              }}
                              aria-label="Generate AI milestones"
                            >
                              <img src={process.env.PUBLIC_URL + "/bright-star.svg"} alt="AI" className={styles.aiIcon} />
                              <span>Generate Milestones</span>
                            </button>
                          )}
                          {loadingAiSuggestions[index] && (
                            <div className={styles.milestoneLoading}>
                              <div className={styles.spinner}></div>
                              <span>Generating...</span>
                            </div>
                          )}
                          {goal.aiSuggestions && (
                            <div className={styles.milestonesGenerated}>
                              <img src={process.env.PUBLIC_URL + "/check-circle.svg"} alt="Generated" className={styles.checkIcon} />
                              <span>Milestones Ready</span>
                            </div>
                          )}
                          <button 
                            className={styles.iconButton} 
                            onClick={() => startEditGoal(index)}
                            aria-label="Edit goal"
                          >
                            <img src={process.env.PUBLIC_URL + "/edit-pencil.svg"} alt="Edit" className={styles.editIcon} />
                          </button>
                          <button 
                            className={styles.iconButton} 
                            onClick={() => handleDeleteGoal(index)}
                            aria-label="Delete goal"
                          >
                            <img src={process.env.PUBLIC_URL + "/trash.svg"} alt="Delete" className={styles.deleteIcon} />
                          </button>
                          <button 
                            className={styles.iconButton} 
                            onClick={() => toggleGoalExpand(index)}
                            aria-label="Toggle details"
                          >
                            <img 
                              src={process.env.PUBLIC_URL + "/nav-arrow-down.svg"} 
                              alt="Expand" 
                              className={`${styles.expandIcon} ${expandedGoals[index] ? styles.expanded : ''}`}
                            />
                          </button>
                        </div>
                      </div>
                      {expandedGoals[index] && (
                        <div className={styles.goalDetails}>
                          <h4 className={styles.detailsHeading}>Detailed Plan</h4>
                          {goal.details && <p className={styles.userDetails}>{goal.details}</p>}
                          
                          <div className={styles.aiSuggestionsSection}>
                            <h4 className={styles.aiSuggestionsHeading}>
                              <img src={process.env.PUBLIC_URL + "/bright-star.svg"} alt="AI" className={styles.aiIcon} /> 
                              AI-Powered Recommendations
                            </h4>
                            
                            {loadingAiSuggestions[index] ? (
                              <div className={styles.loadingSpinner}>
                                <div className={styles.spinner}></div>
                                <p>Generating recommendations (this may take a few minutes)...
                                </p>
                              </div>
                            ) : goal.aiSuggestions ? (
                              <div className={styles.aiSuggestionsContent}>
                                <div className={styles.aiResponse}>
                                  {/* Use Enhanced Milestone Display */}
                                  <EnhancedMilestoneDisplay
                                    milestones={parseAiSuggestions(goal.aiSuggestions)}
                                    goalIndex={index}
                                    highlightedMilestone={highlightedMilestone}
                                    onMilestoneClick={handleMilestoneClick}
                                    aiMetadata={goal.aiMetadata}
                                    aiQuality={goal.aiQuality}
                                  />
                                </div>
                                <button 
                                  className={styles.refreshButton}
                                  onClick={() => requestNewSuggestions(index)}
                                >
                                  Get new recommendations
                                </button>
                              </div>
                            ) : goal.aiMetadata?.error ? (
                              <div className={styles.aiError}>
                                <div className={styles.errorIcon}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                                <div className={styles.errorContent}>
                                  <h5>Failed to generate milestones</h5>
                                  <p>{goal.aiMetadata.error}</p>
                                  <button 
                                    className={styles.retryButton}
                                    onClick={() => getAiSuggestions(index)}
                                  >
                                    Try Again
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className={styles.aiSuggestionsPrompt}>
                                <p>Get personalized AI recommendations on how to achieve this goal.</p>
                                <button 
                                  className={styles.getSuggestionsButton}
                                  onClick={() => getAiSuggestions(index)}
                                >
                                  Generate recommendations
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PathwayPlanner; 